// Filing a billing run: the CSV export, the 837P and its submission record.
//
// Moved out of pages/finance/BillingRun.js. Every function takes the Supabase
// client as its first argument rather than importing it, so the write paths
// can be pinned in tests with src/testUtils/supabaseMock.js.

import { authNumberForFiling, authHistoryByClient, CLAIM_AUTH_TYPE } from '../priorAuth.js';
import { build837P, claimFromLine } from '../edi/x837p.js';
import { CONTROL_NUMBER_RPC } from '../edi/x12.js';
import { preflightRun } from '../edi/preflight.js';
import {
  sha256Hex, controlNumbersOf, submissionFileName, applyAcknowledgement,
  lineStatusAfter, runReopensAfter, RUN_STATUS_WHEN_SENT,
} from '../edi/submission.js';
import { must } from './must.js';

// Used to make `.in()` match nothing when a run has no linked clients.
const NO_ID = '00000000-0000-0000-0000-000000000000';

// ── CSV ─────────────────────────────────────────────────────────────────────

export const CSV_HEADER = ['Member ID','Client Name','Service Date','HCPC Code','Modifier','Units (15-min)','Hours','Billed Amount','Prior Auth Number','Diagnosis Code','AxisCare Visit ID','ICN','Status'];

/**
 * One CSV row per claim line. The authorisation column carries the same
 * number the 837P would send for that line (authNumberForFiling), not the one
 * stored on the line — on 2026-10-19 the two exports were found sending
 * different numbers.
 *
 * @param {object[]} p.lines       the run's claim_lines, in service-date order
 * @param {object}   p.historyBy   client uuid -> prior_auth_history rows
 * @param {object}   p.clientById  client uuid -> { dma_80_pa_number, pa_expiry_date }
 */
export const csvRows = ({ lines = [], historyBy = {}, clientById = {} }) => lines.map(l => [
  l.medicaid_id || '', l.client_name || '', l.service_date || '',
  l.service_code || '', l.modifier || '', l.units ?? '', l.hours || '',
  l.charge_amount || '', authNumberForFiling({ line: l, history: historyBy[l.client_id] || [], client: clientById[l.client_id] }), l.diagnosis_code || '',
  l.axiscare_visit_id || '', l.icn || '', l.status || '',
]);

/**
 * Every cell quoted, rows joined by newlines — the file AHFC has always
 * downloaded. A quote mark inside a value is doubled, as CSV requires; it used
 * to end the cell early and shift every column after it.
 */
export const toCsv = (rows) => [CSV_HEADER, ...rows]
  .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');

export const csvFileName = (destLabel, dateFrom, dateTo) =>
  `${destLabel}_BillingRun_${dateFrom}_to_${dateTo}.csv`;

/**
 * The CSV for a run, or null when it has no lines. The authorisation column
 * is resolved at filing time, the same way as the 837P's.
 */
export const loadRunCsv = async (sb, runId) => {
  const { data } = await sb.from('claim_lines').select('*').eq('billing_run_id', runId).order('service_date');
  if (!data?.length) return null;
  // The same authorization number the 837P would carry for each line.
  const clientIds = [...new Set(data.map(l => l.client_id).filter(Boolean))];
  const [{ data: clients }, { data: authRows }] = await Promise.all([
    sb.from('clients').select('id, dma_80_pa_number, pa_expiry_date')
      .in('id', clientIds.length ? clientIds : [NO_ID]),
    sb.from('prior_auth_history')
      .select('client_id, auth_type, auth_number, submitted_date, effective_date, expiry_date, created_at')
      .eq('auth_type', CLAIM_AUTH_TYPE)
      .in('client_id', clientIds.length ? clientIds : [NO_ID]),
  ]);
  const clientById = Object.fromEntries((clients || []).map(c => [c.id, c]));
  const historyBy = authHistoryByClient(authRows || []);
  return toCsv(csvRows({ lines: data, historyBy, clientById }));
};

// ── 837P ────────────────────────────────────────────────────────────────────

/** A run's submissions, newest first. */
export const loadSubmissions = async (sb, runId) => {
  const { data } = await sb.from('claim_submissions')
    .select('*').eq('billing_run_id', runId).order('generated_at', { ascending: false });
  return data || [];
};

/**
 * Check a GAMMIS run with pre-flight and, if it passes, build its 837P,
 * record the submission and write each claim's control number onto its line.
 *
 * Pre-flight blocks rather than warning. Everything it checks produces a claim
 * that is rejected or paid wrong, and a file that has left the building is
 * harder to take back than a button that refuses. Only call this for a run
 * filesWithGammis() accepts — that guard stays with the button.
 *
 * Throws on a failed read or write; the caller shows the message.
 *
 * @returns {{ blocking: string[], warnings: string[] }
 *          | { edi: string, fileName: string, warnings: string[] }}
 */
export const generate837P = async (sb, { runId, run, identity, dateFrom, dateTo, profile }) => {
  const { data: lines, error } = await sb
    .from('claim_lines').select('*').eq('billing_run_id', runId).order('service_date');
  if (error) throw new Error(`Could not load the claim lines: ${error.message}`);
  if (!lines?.length) throw new Error('This run has no claim lines.');

  // The subscriber loop needs date of birth, gender and address, none of
  // which live on claim_lines.
  const clientIds = [...new Set(lines.map(l => l.client_id).filter(Boolean))];
  const clients = await must(sb.from('clients')
    .select('id, first_name, last_name, medicaid_id, date_of_birth, gender, address, city, state, zip, dma_80_pa_number, pa_expiry_date, service_type')
    .in('id', clientIds.length ? clientIds : [NO_ID]), "load the run's clients");
  const authRows = await must(sb.from('prior_auth_history')
    .select('client_id, auth_type, auth_number, submitted_date, effective_date, expiry_date, created_at').eq('auth_type', CLAIM_AUTH_TYPE),
    'load the authorisation history');
  const catalog = await must(sb.from('service_catalog')
    .select('code, billable, visit_source, procedure_code, active'), 'load the service catalog');

  // Each client's newest eligibility check, for pre-flight's coverage warning.
  const checks = await must(sb.from('eligibility_checks')
    .select('client_id, result, managed_care_org, coverage_end, coverage_end_is_plan_end, reject_reason, checked_at, last_confirmed_at')
    .in('client_id', clientIds.length ? clientIds : [NO_ID]).order('checked_at', { ascending: false }),
    'load the eligibility checks');
  const eligibility = {};
  for (const c of checks || []) if (!eligibility[c.client_id]) eligibility[c.client_id] = c;
  const { data: automationOn } = await sb.rpc('eligibility_automation_on');

  const check = preflightRun({
    run, lines, clients: clients || [],
    authHistory: authHistoryByClient(authRows || []),
    identity, serviceCatalog: catalog || [],
    eligibility, eligibilityAutomationOn: automationOn === true,
  });
  if (!check.ok) {
    // The identity banner above already carries identityIssues, so they
    // are not repeated here — they would bury the ones that belong to this
    // run. But when identity is the ONLY thing blocking, runIssues is
    // empty and the panel would render nothing at all: the button would
    // look like it had done nothing rather than like it had refused.
    const blocking = check.runIssues.length
      ? check.runIssues
      : ['The billing identity is incomplete, so no file can be built. See the banner above this list.'];
    return { blocking, warnings: check.warnings };
  }

  const byId = Object.fromEntries((clients || []).map(c => [c.id, c]));
  const byClient = authHistoryByClient(authRows || []);

  // Resolve the authorisation AT FILING TIME rather than trusting what the
  // run recorded. A line generated after an authorisation lapsed carries no
  // number, and filing with the expired one denies for "not valid for these
  // dates" instead of "none on file" — more specific, more appealable, and
  // it reprocesses on its own if the renewal is dated to cover the gap.
  //
  // The claim_line is NOT rewritten. It records what was authorised, which
  // was nothing; claim_submissions.edi_content records what was sent. Those
  // are different facts and collapsing them would make the run lie about
  // the authorisation it had.
  const claims = lines.map(l => claimFromLine({
    ...l,
    prior_auth_number: authNumberForFiling({ line: l, history: byClient[l.client_id] || [], client: byId[l.client_id] }),
  }, byId[l.client_id]));
  // ISA13 comes from the database counter so no two files ever share one.
  const { data: controlNumber, error: ctlErr } = await sb.rpc(CONTROL_NUMBER_RPC);
  if (ctlErr) throw new Error(`Could not get a control number for the file: ${ctlErr.message}`);
  const edi = build837P({
    identity, claims, controlNumber,
    batchRef: `BR${dateFrom.replace(/-/g, '')}`,
    usageIndicator: identity.usageIndicator,
  });

  // The attempt number in the file name counts the run's files in the
  // database. It used to count the ones the screen had loaded, which was none
  // when the 837P was pressed from the wizard, so a second file could repeat
  // the first one's name.
  const earlier = await must(sb.from('claim_submissions').select('id').eq('billing_run_id', runId),
    "count the run's earlier files");
  const attempt = (earlier || []).length + 1;

  const controls = controlNumbersOf(edi);
  const fileName = submissionFileName({
    dateFrom, dateTo,
    usageIndicator: controls.usage_indicator, attempt,
  });

  const { data: sub, error: subErr } = await sb.from('claim_submissions').insert({
    billing_run_id: runId,
    channel: 'web_portal',
    status: 'generated',
    ...controls,
    claim_count: claims.length,
    total_charge: claims.reduce((t, c) => t + (c.chargeAmount || 0), 0).toFixed(2),
    edi_content: edi,
    file_sha256: await sha256Hex(edi),
    file_name: fileName,
    generated_by: profile?.id || null,
    generated_by_name: profile?.full_name || '',
  }).select('id').single();
  if (subErr) throw new Error(`Could not record the submission: ${subErr.message}`);

  // Persist each claim's control number. This is CLM01, echoed back on the
  // 835 and on a claim-level 824 rejection, and it is what turns remittance
  // matching from a date-and-amount guess into a lookup.
  // If one of these fails the file is already recorded, so say exactly that:
  // a line without its control number cannot be matched to its remittance.
  for (const [i, l] of lines.entries()) {
    await must(sb.from('claim_lines').update({
      submission_id: sub.id,
      claim_control_number: claims[i].controlNumber,
    }).eq('id', l.id), `write the control number onto ${l.client_name || 'a line'} ${l.service_date || ''} (the file itself was recorded)`);
  }

  return { edi, fileName, warnings: check.warnings };
};

// ── After the file is built ─────────────────────────────────────────────────

/**
 * "Sent" is a real state, not an assumption. A file generated and never
 * uploaded is exactly the failure this flag exists to make visible.
 */
export const markSubmissionSent = async (sb, { sub, profile, now }) => {
  await must(sb.from('claim_submissions').update({
    status: 'sent', sent_at: now,
    sent_by: profile?.id || null, sent_by_name: profile?.full_name || '',
  }).eq('id', sub.id), 'mark the file uploaded');
  await must(sb.from('claim_lines')
    .update({ status: 'submitted', submitted_at: now })
    .eq('submission_id', sub.id).eq('status', 'approved'), "mark the file's lines submitted");
  await must(sb.from('billing_runs')
    .update({ submitted_at: now }).eq('id', sub.billing_run_id), 'stamp the run submitted');
  await must(sb.from('billing_runs')
    .update({ status: RUN_STATUS_WHEN_SENT }).eq('id', sub.billing_run_id).eq('status', 'approved'), 'mark the run submitted');
};

/**
 * Queue a generated file for SFTP. The channel is set here rather than at
 * generation because generating a file is not the same as deciding to file
 * it: a run can be exported, read, and thought better of.
 * @returns {{ error: object|null }}
 */
export const queueForSftp = async (sb, subId) => {
  const { error } = await sb.from('claim_submissions')
    .update({ channel: 'sftp' }).eq('id', subId);
  return { error: error || null };
};

/**
 * Read an acknowledgement (TA1 or 824) for a submission and record what it
 * says. A rejection puts the file's lines back to approved and reopens the
 * run, unless another of its files is still live.
 *
 * @returns the applyAcknowledgement() result; nothing is written when !ok
 */
export const recordAcknowledgement = async (sb, { sub, text }) => {
  const result = applyAcknowledgement(text);
  if (!result.ok) return result;
  await must(sb.from('claim_submissions').update(result.update).eq('id', sub.id), 'record the acknowledgement');
  const lineStatus = lineStatusAfter(result.update.status);
  if (lineStatus) {
    await must(sb.from('claim_lines').update({ status: lineStatus }).eq('submission_id', sub.id), "update the file's lines");
  }
  const siblings = await must(sb.from('claim_submissions').select('status')
    .eq('billing_run_id', sub.billing_run_id).neq('id', sub.id), "read the run's other files");
  if (runReopensAfter(result.update.status, (siblings || []).map(x => x.status))) {
    await must(sb.from('billing_runs').update({ status: 'approved' })
      .eq('id', sub.billing_run_id).eq('status', RUN_STATUS_WHEN_SENT), 'reopen the run');
  }
  return result;
};
